import React from "react";
import { TrendingUp, Users, CalendarCheck2 } from "lucide-react";
import { useInView, useCountUp } from "../hooks/useCountUp";
import Reveal from "./Reveal";

function StatCard({ icon: Icon, value, suffix, decimals, label, sub, accent }) {
  // re-trigger every time the card enters the viewport
  const [ref, inView] = useInView({ threshold: 0.35, once: false });
  const animated = useCountUp(value, 1800, inView, decimals);
  const display =
    decimals > 0 ? animated.toFixed(decimals) : Math.round(animated).toLocaleString();

  return (
    <div
      ref={ref}
      className="relative rounded-[28px] p-7 md:p-8 overflow-hidden border border-neutral-200/60"
      style={{
        background: accent
          ? "linear-gradient(160deg, #ff3301 0%, #d62a00 100%)"
          : "#ffffff",
        color: accent ? "#fff" : "#0A0A0A",
        minHeight: 240,
      }}
    >
      {!accent && (
        <div
          className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(255,51,1,0.12) 0%, rgba(255,51,1,0) 100%)",
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: accent ? "rgba(255,255,255,0.18)" : "rgba(255,51,1,0.10)",
            color: accent ? "#fff" : "var(--agero-orange)",
          }}
        >
          <Icon size={18} />
        </div>
        {sub && (
          <span
            className="text-[11px] font-semibold tracking-wider uppercase"
            style={{ color: accent ? "rgba(255,255,255,0.85)" : "#9a9a9a" }}
          >
            {sub}
          </span>
        )}
      </div>

      <div
        className="font-display mt-5"
        style={{
          fontSize: "clamp(52px, 5.6vw, 80px)",
          letterSpacing: "-0.05em",
          lineHeight: 1,
          color: accent ? "#fff" : "var(--agero-orange)",
          fontWeight: 800,
        }}
      >
        {display}
        <span>{suffix}</span>
      </div>

      <div
        className="mt-4 text-[14.5px] leading-snug"
        style={{ color: accent ? "rgba(255,255,255,0.92)" : "#525252" }}
      >
        {label}
      </div>

      {/* tiny inline bar viz — re-animates each time in view */}
      <div className="mt-5 flex items-end gap-1.5 h-6" aria-hidden="true">
        {[35, 55, 42, 70, 58, 82, 68, 95].map((h, i) => (
          <span
            key={i}
            className="flex-1 rounded-sm"
            style={{
              height: `${inView ? h : 0}%`,
              background: accent ? "rgba(255,255,255,0.55)" : "var(--agero-orange)",
              opacity: accent ? 0.85 : 0.7,
              transition: `height ${600 + i * 70}ms cubic-bezier(.2,.8,.2,1) ${i * 60}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function StatsCounter() {
  return (
    <section className="section" id="stats">
      <div className="container-x">
        <Reveal>
          <div className="text-center mb-14">
            <div className="font-script" style={{ fontSize: 26, color: "var(--agero-orange)" }}>
              (The numbers)
            </div>
            <h2
              className="font-display mt-3"
              style={{
                fontSize: "clamp(36px, 5.5vw, 78px)",
                lineHeight: 1.02,
                letterSpacing: "-0.04em",
                maxWidth: 1100,
                margin: "12px auto 0",
              }}
            >
              One engagement. 349 days.
              <br />
              <span className="gradient-orange-text">No paid promotion.</span>
            </h2>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          <StatCard
            icon={TrendingUp}
            value={10.19}
            suffix="M"
            decimals={2}
            label="Total impressions generated organically across one client engagement."
            sub="Impressions"
          />
          <StatCard
            icon={Users}
            value={242}
            suffix="%"
            decimals={0}
            label="Follower growth for one client across 349 days. 8,358 to 28,592 followers."
            sub="Audience growth"
            accent
          />
          <StatCard
            icon={CalendarCheck2}
            value={96}
            suffix="%"
            decimals={0}
            label="Posting consistency. 335 of 349 days published, zero paid promotion."
            sub="Consistency"
          />
        </div>
      </div>
    </section>
  );
}
